import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck } from "lucide-react";
import { useBrands, useBrand } from "../api/hooks";
import { OfferCard, BrandLogo } from "../components/OfferCard";
import { GridSkeleton, ErrorState, EmptyState } from "../components/States";
import { MaskedLine } from "../components/Motion";
import { Seo } from "../components/Seo";

const BrandPage = () => {
    const { slug } = useParams();
    const { data: brands, isLoading: brandsLoading } = useBrands();
    const listed = brands?.find((b) => b.slug === slug);
    const { data, isLoading, isError, refetch } = useBrand(listed?.id);

    const brand = data?.brand || listed;
    const offers = (data?.offers || []).map((o) => ({ ...o, brand: o.brand || brand }));
    const loading = brandsLoading || (listed && isLoading);

    return (
        <>
            <Seo
                title={brand ? `${brand.name} offers & coupons — SahiKhoj` : "Brand — SahiKhoj"}
                description={brand ? `Live ${brand.name} deals, coupons and offers on SahiKhoj.` : "Brand offers on SahiKhoj."}
                path={`/brands/${slug}`}
                crumbs={[
                    { name: "Home", path: "/" },
                    { name: "Brands", path: "/brands" },
                    { name: brand?.name || slug, path: `/brands/${slug}` },
                ]}
            />
            <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <Link to="/brands" data-testid="brand-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-mist transition-colors hover:text-saffron">
                        <ArrowLeft className="h-4 w-4" />
                        All brands
                    </Link>
                    <div className="mt-6 flex items-center gap-5">
                        <span className="rounded-2xl bg-white p-2">
                            <BrandLogo brand={brand} className="h-12 w-12" />
                        </span>
                        <div>
                            <h1 className="font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl" data-testid="brand-name">
                                <MaskedLine delay={0.15}>{brand?.name || slug}</MaskedLine>
                            </h1>
                            {brand?.isFeatured && (
                                <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-saffron">
                                    <BadgeCheck className="h-3.5 w-3.5" /> Featured brand
                                </span>
                            )}
                        </div>
                    </div>
                    {brand?.description && (
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">{brand.description}</p>
                    )}
                </div>
            </section>

            <section className="bg-cream py-14 sm:py-20" data-testid="brand-page">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    {loading && <GridSkeleton count={8} />}
                    {!loading && isError && <ErrorState onRetry={refetch} />}
                    {!loading && !isError && !listed && (
                        <EmptyState title="Brand not found" message="This brand may have been removed." />
                    )}
                    {!loading && listed && !isError && offers.length === 0 && (
                        <EmptyState
                            title={`No live ${brand?.name || ""} offers right now`}
                            message="Offers from this brand will appear here as they go live."
                        />
                    )}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {offers.map((o) => (
                            <OfferCard key={o.id} offer={o} />
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default BrandPage;
